import { useRouter } from "next/router";
import {
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import AutoSuggest from "react-autosuggest";
import {
  AllBusiness,
  groupedBusiness,
  groupedSubtype,
  propertyTypeOptions,
  subTypeOptions,
} from "../Listing/data";
import {
  StyledBigSearcher,
  InnerSearchWrapper,
  SearchWrapper,
  LoadingIndicator,
  StyledMobileSearchBar,
  TriggerFiltersWrapper,
  SearchButton,
  TriggerFiltersButton,
  Overlay,
} from "./StyledBigSearcher";
import {
  Grid,
  GridItem,
  Text,
  Image as ChakraImage,
  Avatar,
  Spinner,
  Button,
  Stack,
  Box,
  Flex,
  Checkbox,
  IconButton,
  Collapse,
  Divider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverCloseButton,
  PopoverFooter,
  PopoverBody,
  PopoverArrow,
  PopoverHeader,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useQuery,
  ModalCloseButton,
} from "@chakra-ui/react";
import * as Sentry from "@sentry/nextjs";
import slugify from "slugify";

import { clientError, env_check, isProduction } from "../../utils/utilties";
import {
  CheckCircleIcon,
  SmallAddIcon,
  SmallCloseIcon,
} from "@chakra-ui/icons";
import { IoIosArrowDown } from "react-icons/io";
import { QueryStateContext } from "../../utils/contexts/QueryStateContext";
import { AiOutlineArrowRight } from "react-icons/ai";
import useWindowSize from "../Utility/windowSize";
import { useDebouce } from "../../utils/clientUtilities";
import { MdSearch } from "react-icons/md";
import { getGeocode } from "use-places-autocomplete";
import { useLoadScript } from "@react-google-maps/api";
const libraries = ["places"];

const sectionTitles = [
  "Property Types",
  "Businesses",
  "Places",
  "Listings",
  "People",
] as const;

export type ISuggestions = SuggestionSection[];
export interface SuggestionSection {
  title: typeof sectionTitles[number];
  items: SuggestedItem[];
}
export interface SuggestedItem {
  value: string;
  photoURL?: string;
  industryRole?: string;
  // sectionReference here points to the section that the suggestion belongs to
  sectionReference?: typeof sectionTitles[number];
  queryStringKey?: string;
  profileLink?: string;
  propOrSubTypeLink?: string;
  businessLink?: string;
  location?: string;
  placeId?: string;
  imageLink?: string;
  label?: string;
  urlValue?: string;
  listingId?: number;
  subject?: string;
}

interface BigSearcherProps {
  formFactor?: "mobile";
  showOverlay?: boolean;
  showTypeSelector?: boolean;
  panTo?: any;
  peopleOnly?: boolean
}


function BigSearcher(props: BigSearcherProps) {
  const { propertyTypeChecked, subTypeChecked, queryState, setQueryState } =
    useContext(QueryStateContext);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_MAPS_API_KEY,
    // @ts-ignore
    libraries,
  });

  const formatInitialSuggestions = () => {
    const formattedPropertyTypes: SuggestedItem[] = propertyTypeOptions.map(
      (propertyType) => ({
        value: propertyType.value,
        sectionReference: "Property Types",
        propOrSubTypeLink: `/listings/${slugify(propertyType.value)}`,
        queryStringKey: "propertyType",
      })
    );
    const formattedSubTypes: SuggestedItem[] = subTypeOptions
      .filter((subType) => subType.group.toLowerCase() !== "sale of business")
      .map((subType) => ({
        value: subType.value,
        sectionReference: "Property Types",
        propOrSubTypeLink: `/listings/${slugify(
          subType.urlValue || subType.value
        )}`,
        queryStringKey: "subType",
      }));
    const formattedBusinessTypes: SuggestedItem[] = AllBusiness.map(
      (businessType) => ({
        value: businessType.value,
        sectionReference: "Businesses",
        businessLink: `/listings/${slugify(businessType.value)}`,
        queryStringKey: "businessType",
      })
    );
    const formattedBusinessCategories: SuggestedItem[] = groupedBusiness.map(
      (businessCategory) => ({
        value: businessCategory.value,
        sectionReference: "Businesses",
        businessLink: `/listings/${slugify(businessCategory.urlValue)}`,
        queryStringKey: "businessCategory",
      })
    );

    return [
      {
        title: "Property Types",
        items: [...formattedPropertyTypes, ...formattedSubTypes],
      },
      {
        title: "Businesses",
        items: [...formattedBusinessCategories, ...formattedBusinessTypes],
      },
    ] as ISuggestions;
  };
  const initialSuggestions: ISuggestions = useMemo(
    () => props.peopleOnly ? null : formatInitialSuggestions(),
    []
  );

  const [suggestions, setSuggestions] = useState(props.peopleOnly ? [] : initialSuggestions);
  const [fetchStatus, setFetchStatus] = useState<"loading" | "idle" | "error">(
    "idle"
  );
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const modInputRef = useRef(inputRef.current?.autowhatever?.input);
  const [showModal, setShowModal] = useState(false);
  const [formFactor, setFormFactor] = useState(props.formFactor);
  const size = useWindowSize();

  const handleInputChange = (e, { newValue, method }) => {
    // Don't update the input text as the user navigates through the suggestions with the up and down arrow keys
    if (method === "up" || method === "down") {
      return;
    }
    setInputValue(newValue);
  };
  const handleSubmit = () => {
    setShowModal(false);
    const _queryState = { ...queryState };

    const filters = {
      ..._queryState,
    };
    let propertyTypeArr = [];
    let subTypeArr = [];

    Object.keys(propertyTypeChecked).forEach((key) => {
      if (propertyTypeChecked[key]) {
        propertyTypeArr.push(key);
      }
    });
    Object.keys(subTypeChecked).forEach((key) => {
      if (subTypeChecked[key]?.length > 0) {
        subTypeArr = [...subTypeArr, ...subTypeChecked[key]];
      }
    });

    // if (propertyTypeArr.length)
    filters["propertyType"] = propertyTypeArr;
    // if (subTypeArr.length)
    filters["subType"] = subTypeArr;
    if (inputValue) filters["keyword"] = inputValue;

    setQueryState(filters);
    router.push({
      pathname: `/listings`,
      query: filters,
    });
  };
  const router = useRouter();

  const propertyOrBusinessSuggestions = props.peopleOnly ? [] : useMemo(() => {
    const suggestions = initialSuggestions
      .slice(0)
      .map((suggestion) => {
        return {
          ...suggestion,
          items: suggestion.items
            .filter((item) => {
              const itemFragments = item.value
                .trim()
                .toLowerCase()
                .replace(/ /g, " ")
                .split(" ");
              const searchQueryFragments = inputValue
                .trim()
                .toLowerCase()
                .replace(/ /g, " ")
                .split(" ");

              return itemFragments.some((fragment) => {
                return searchQueryFragments.some((qFragment) => {
                  return (
                    qFragment.includes(fragment) || fragment.includes(qFragment)
                  );
                });
              });
            })
            .slice(0, 5),
        };
      })
      .filter((suggestion) => suggestion.items.length > 0);
    setSuggestions(suggestions);
    return suggestions;
  }, [inputValue]);

  // If true, when the user clicks on properties/businesses suggestions we take the user to the client-side rendered page; otherwise, we take the user to the static page
  const isOnListingsPage = useMemo(() => {
    if (!router.pathname.includes("/listings")) {
      return false;
    }
    // Gets the id param and checks if it's a number (listingId) to see if the current page is an individual listing page
    const idParam = router.asPath.split("/")[2];
    const idIsNotANumber = isNaN(Number(idParam));
    return idIsNotANumber;
  }, [router.pathname]);

  async function getMatches(value): Promise<any> {
    setFetchStatus("loading");
    try {
      const apiResponse = await fetch(`/api/search${props.peopleOnly ? "?peopleOnly=true" : ""}`, {
        method: "GET",
        headers: {
          query: value,
        },
      });
      const jsonData = await apiResponse.json();

      if (apiResponse.status === 201) {
        // no data was found for the search query
        // Handle it here
      }
      return jsonData.data;
    } catch (error) {
      setFetchStatus("error");
      clientError(error);
    }
  }

  async function getSuggestions(value) {
    let data = await getMatches(value);
    let _suggestions = [];
    if (data.length) {
      const placesSuggestions = data.find(({ title }) => title === "Places");
      if (!!placesSuggestions) {
        _suggestions.push(placesSuggestions);
      }
      const otherSuggestions = data.filter(({ title }) => title !== "Places");
      _suggestions.push(...propertyOrBusinessSuggestions, ...otherSuggestions);
      setSuggestions(_suggestions);
    }

    setFetchStatus("idle");
  }

  async function getBounds(placeId) {
    // Badges
    let areaFilterTags = [];
    let tempBounds = [];

    const results = await getGeocode({ placeId });
    const { formatted_address, geometry } = results[0];
    areaFilterTags.push({
      description: formatted_address.replace(", Canada", ""),
      place_id: placeId,
    });
    let boundObj = results[0]["geometry"]["bounds"]
      ? results[0]["geometry"]["bounds"]
      : results[0]["geometry"]["viewport"];

    let ne = boundObj.getNorthEast();
    let sw = boundObj.getSouthWest();
    tempBounds.push([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);

    // setBigSearchBadges(tempBadges);
    // Send bounds
    const { bounds, page, ...rest } = queryState;
    let newBoundObj = {
      areaFilterTags,
      tempBounds, // the actual bounds
      geometry,
      // thisLocationBounds: geometry.bounds ? geometry.bounds : geometry.viewport
    };
    return newBoundObj;
  }

  async function onPlaceSuggestionSelected(suggestion: SuggestedItem) {
    const { placeId } = suggestion;

    const updatedQuery = { ...queryState };
    let boundsObj = {
      areaFilterTags: [],
      tempBounds: [],
      geometry: undefined
      // thisLocationBounds: []
    };
    if (Array.isArray(updatedQuery["placeId"])) {
      const queryFilterExists = updatedQuery["placeId"].includes(placeId);
      if (!queryFilterExists) {
        boundsObj = await getBounds(placeId);
        updatedQuery["placeId"].push(placeId);
      }
    } else {
      const placeIdInQueryString = updatedQuery["placeId"];
      const queryFilterExists = placeIdInQueryString === placeId;
      if (queryFilterExists) {
        updatedQuery["placeId"] = [placeIdInQueryString];
      } else {
        boundsObj = await getBounds(placeId);
        updatedQuery["placeId"] = [placeId, placeIdInQueryString];
      }
    }
    let propertyTypeArr = [];
    let subTypeArr = [];

    Object.keys(propertyTypeChecked).forEach((key) => {
      if (propertyTypeChecked[key]) {
        propertyTypeArr.push(key);
      }
    });
    Object.keys(subTypeChecked).forEach((key) => {
      if (subTypeChecked[key]?.length > 0) {
        subTypeArr = [...subTypeArr, ...subTypeChecked[key]];
      }
    });

    updatedQuery["propertyType"] = propertyTypeArr;
    updatedQuery["subType"] = subTypeArr;

    const { tempBounds, areaFilterTags, geometry } = boundsObj;
    if (areaFilterTags.length && tempBounds.length) {
      updatedQuery["bounds"] = {
        ...queryState["bounds"],
        actualBounds: queryState?.bounds?.actualBounds
          ? [...queryState.bounds.actualBounds, ...tempBounds]
          : [...tempBounds],
        areaFilterTags: queryState?.bounds?.areaFilterTags
          ? [...queryState.bounds.areaFilterTags, ...areaFilterTags]
          : [...areaFilterTags],
      };
    }

    setQueryState(updatedQuery);

    if (isOnListingsPage) {
      props?.panTo(geometry);
      router.replace(
        {
          pathname: "/listings",
          query: updatedQuery,
        },
        undefined,
        { shallow: true }
      );

    } else {
      router.push({
        pathname: "/listings",
        query: updatedQuery,
      });
    }
  }
  function onListingSuggestionSelected(suggestion: SuggestedItem) {
    const { subject, listingId } = suggestion;

    router.push({
      pathname: `/listings/${listingId}/${slugify(subject)}`,
    });
  }
  function onPropertyTypeSuggestionSelected(suggestion: SuggestedItem) {
    const { propOrSubTypeLink, queryStringKey, value } = suggestion;
    if (isOnListingsPage) {
      const updatedQuery = { ...queryState };
      if (Array.isArray(updatedQuery[queryStringKey])) {
        const queryFilterExists = updatedQuery[queryStringKey].includes(value);
        if (!queryFilterExists) {
          updatedQuery[queryStringKey].push(value);
        }
      } else {
        const propertyTypeInQueryState = updatedQuery[queryStringKey];
        const queryFilterExists = propertyTypeInQueryState === value;
        if (queryFilterExists) {
          updatedQuery[queryStringKey] = [propertyTypeInQueryState];
        } else {
          updatedQuery[queryStringKey] = [value, propertyTypeInQueryState];
        }
      }
      setQueryState(updatedQuery);
      router.replace(
        {
          pathname: "/listings",
          query: updatedQuery,
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    router.push({
      pathname: propOrSubTypeLink,
    });
  }
  function onBusinessSuggestionSelected(suggestion: SuggestedItem) {
    const { businessLink, value, queryStringKey } = suggestion;
    if (isOnListingsPage) {
      const updatedQuery = { ...queryState };
      if (Array.isArray(updatedQuery[queryStringKey])) {
        const queryFilterExists = updatedQuery[queryStringKey].includes(value);
        if (!queryFilterExists) {
          updatedQuery[queryStringKey].push(value);
        }
      } else {
        const businessInQueryState = updatedQuery[queryStringKey];
        const queryFilterExists = businessInQueryState === value;
        if (queryFilterExists) {
          updatedQuery[queryStringKey] = [businessInQueryState];
        } else {
          updatedQuery[queryStringKey] = [value, businessInQueryState];
        }
      }
      setQueryState(updatedQuery);
      router.replace(
        {
          pathname: "/listings",
          query: updatedQuery,
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    router.push({
      pathname: businessLink,
    });
  }
  function onPeopleSuggestionSelected(suggestion: SuggestedItem) {
    const { profileLink } = suggestion;
    router.push({
      pathname: profileLink,
    });
  }

  // The keys in suggestionSelectedHandlerMap correspond to section titles
  const suggestionSelectedHandlerMap: Record<
    typeof sectionTitles[number],
    (suggestion: SuggestedItem) => void
  > = {
    Places: onPlaceSuggestionSelected,
    "Property Types": onPropertyTypeSuggestionSelected,
    Listings: onListingSuggestionSelected,
    Businesses: onBusinessSuggestionSelected,
    People: onPeopleSuggestionSelected,
  };

  // runs the getSuggestions function after every 300ms delay between user input
  const getSuggestionsDebounced = useDebouce(getSuggestions, 300);

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = (suggestion) => {
    return suggestion.value;
  };

  // Autosuggest will call this function every time you need to update suggestions.
  const onSuggestionsFetchRequested = ({ value, reason }) => {
    // prevents us from fetching if the input gains focus
    if (reason === "input-focused") return;
    getSuggestionsDebounced(value);
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  // Runs when the selection is selected
  const onSuggestionSelected = (event, { suggestion }) => {
    setInputValue("");
    if (env_check) {
      // @ts-ignore
      window?.analytics?.track("Search suggestion selected", {
        searchQuery: inputValue,
        suggestionTitle: suggestion.value,
        suggestiontype: suggestion.sectionReference,
      });
    }
    if (showModal) {
      setShowModal(false);
    }
    suggestionSelectedHandlerMap[suggestion.sectionReference](suggestion);
  };

  const renderSectionTitle = (section) => {
    return section.title;
  };

  const renderSuggestion = (suggestion: SuggestedItem, { query }) => {
    switch (suggestion.sectionReference) {
      case "Listings":
        return <ListingSuggestion suggestion={suggestion} query={query} />;
      case "People":
        return <PeopleSuggestion suggestion={suggestion} query={query} />;
      case "Businesses":
      case "Property Types":
      default:
        return <SuggestionItem suggestion={suggestion} query={query} />;
    }
  };

  const getSectionSuggestions = (section) => {
    return section.items;
  };

  const inputProps = {
    placeholder: props.peopleOnly ? "Search by agent name" : "Location, description or agent name",
    value: inputValue,
    onChange: handleInputChange,
  };

  useEffect(() => {
    setFormFactor(props.formFactor);
  }, [props.formFactor]);

  const MobileSearchBar = (
    <SearchWrapper
      peopleOnly={props.peopleOnly}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      mobileLayout={formFactor === "mobile" || showModal}
    >
      <InnerSearchWrapper mobileLayout
        peopleOnly={props.peopleOnly}
      >
        <MdSearch
          // padding="8px"
          color="#125f3f"
          size="24px"
        />
        <StyledBigSearcher mobileLayout>
            {isLoaded &&
          <AutoSuggest
            multiSection={true}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            renderSectionTitle={renderSectionTitle}
            getSectionSuggestions={getSectionSuggestions}
            onSuggestionSelected={onSuggestionSelected}
            inputProps={inputProps}
            ref={inputRef}
          />
            }
        </StyledBigSearcher>
        {fetchStatus === "loading" && (
          <LoadingIndicator>
            <Spinner size="md" color="#a6a6a6" thickness="3px" />
          </LoadingIndicator>
        )}
      </InnerSearchWrapper>
      <ToggleFilters onSubmit={handleSubmit} mobileLayout />
      {!props.peopleOnly &&
      <SearchButton type="submit" mobileLayout>
        <span>Search</span>
        <AiOutlineArrowRight />
      </SearchButton>
      }
      {props.showOverlay && <Overlay onClick={() => setShowModal(false)} />}
    </SearchWrapper>
  );

  useEffect(() => {
    const inputEl = inputRef?.current?.autowhatever?.input;
    if (inputRef.current) {
      inputEl?.addEventListener("click", (e) => {
        if (size.width <= 550 || formFactor === "mobile") {
          setShowModal(true);
        }
      });
    }
    return () => {
      inputEl?.removeEventListener("click", (e) => {
        if (size.width <= 550 || formFactor === "mobile") {
          setShowModal(true);
        }
      });
    };
  }, [inputRef.current]);

  useEffect(() => {
    const bodyEl = document.querySelector("body");
    if (props.showOverlay && showModal) {
      bodyEl.style.overflow = "hidden";
    } else {
      bodyEl.style.overflow = "visible";
    }
  }, [props.showOverlay, showModal]);

  if (props.peopleOnly || (!showModal && formFactor !== "mobile")) {
    return (
      <SearchWrapper
        peopleOnly={props.peopleOnly}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <InnerSearchWrapper
          peopleOnly={props.peopleOnly}
        >
          {props.showTypeSelector ? (
            <ToggleFilters onSubmit={handleSubmit} />
          ) : (
            <MdSearch
              // padding="8px"
              color="#125f3f"
              size="24px"
            />
          )}
          <StyledBigSearcher>
            {isLoaded && 
            <AutoSuggest
              multiSection={true}
              getSuggestionValue={getSuggestionValue}
              renderSuggestion={renderSuggestion}
              suggestions={suggestions}
              onSuggestionsFetchRequested={onSuggestionsFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              renderSectionTitle={renderSectionTitle}
              getSectionSuggestions={getSectionSuggestions}
              onSuggestionSelected={onSuggestionSelected}
              inputProps={inputProps}
              ref={inputRef}
            />
            }
          </StyledBigSearcher>
          {fetchStatus === "loading" && (
            <LoadingIndicator>
              <Spinner size="md" color="#a6a6a6" thickness="3px" />
            </LoadingIndicator>
          )}
        </InnerSearchWrapper>
        {!props.peopleOnly &&
          <SearchButton type="submit">
            <span>Search</span>
            <AiOutlineArrowRight />
          </SearchButton>
        }
      </SearchWrapper>
    );
  }
  if (formFactor === "mobile" || showModal) {
    return <>{MobileSearchBar}</>;
  }
}

export default BigSearcher;

const SuggestionItem = ({ suggestion, query }) => {

  return (
    <div>
      {suggestion.value}
    </div>
  );
};

const PeopleSuggestion = ({ suggestion, query }) => {
  return (
    <Grid
      templateAreas={`
            "user-image user-fullname"
            "user-image user-fullname"
            "user-image user-industryRole"
        `}
      gridTemplateRows={"1fr 1fr 20px"}
      gridTemplateColumns={"40px 1fr"}
      maxH="40px"
      columnGap={"13px"}
    >
      <GridItem
        gridArea={"user-image"}
        alignSelf={"center"}
        width="40px"
        height="40px"
      >
        <Avatar src={suggestion?.photoURL} w="40px" h="40px" />
      </GridItem>
      <GridItem
        gridArea={"user-fullname"}
        fontSize="1.05rem"
        justifySelf={"start"}
        alignSelf={"start"}
        overflowX="hidden"
      >
        <Text noOfLines={1} isTruncated>
          {suggestion.value}
        </Text>
      </GridItem>
      <GridItem
        gridArea={"user-industryRole"}
        color="#858585"
        fontSize="13px"
        alignSelf={"end"}
      >
        <Text noOfLines={1} isTruncated>
          {suggestion.industryRole}
        </Text>
      </GridItem>
    </Grid>
  );
};

const ListingSuggestion = ({ suggestion, query }) => {
  return (
    <Grid
      templateAreas={`
                "listing-image listing-subject"
                "listing-image listing-subject"
                "listing-image listing-location"
            `}
      gridTemplateRows={"1fr 1fr 20px"}
      gridTemplateColumns={"80px 1fr"}
      maxH="40px"
      columnGap={"13px"}
    >
      <GridItem gridArea={"listing-image"} width="80px" height="40px">
        <ChakraImage
          src={suggestion.imageLink || ""}
          width="100%"
          height="100%"
        />
      </GridItem>
      <GridItem
        gridArea={"listing-subject"}
        fontSize="1.05rem"
        alignSelf={"start"}
        overflowX="hidden"
      >
        <Text lineHeight={"20px"} noOfLines={1} isTruncated>
          {suggestion.value}
        </Text>
      </GridItem>
      <GridItem
        gridArea={"listing-location"}
        color="#858585"
        fontSize="13px"
        alignSelf={"end"}
      >
        <Text lineHeight={"15px"} noOfLines={1} isTruncated>
          {suggestion.location}
        </Text>
      </GridItem>
    </Grid>
  );
};
interface ToggleFiltersProps {
  mobileLayout?: boolean;
  onSubmit: any;
  setShowModal?: any;
}

const ToggleFilters = (props: ToggleFiltersProps) => {
  const {
    propertyTypeChecked,
    setPropertyTypeChecked,
    subTypeChecked,
    setSubTypeChecked,
    setExpandedSubTypes,
  } = useContext(QueryStateContext);
  const selectedFilters = useMemo(() => {
    const activePropertyTypeFilters = Object.values(propertyTypeChecked).filter(
      (a) => a === true
    );
    const activeSubTypeFilters = Object.values(subTypeChecked).filter(
      (b: any) => b.length
    );
    let displayFilter;
    if (activePropertyTypeFilters.length) {
      displayFilter = Object.keys(propertyTypeChecked).find(
        (a) => propertyTypeChecked[a] === true
      );
    } else if (activeSubTypeFilters.length) {
      displayFilter = Object.values(subTypeChecked).find(
        (a: any) => a.length
      )[0];
    }
    return {
      count: activePropertyTypeFilters.length + activeSubTypeFilters.length,
      displayFilter,
    };
  }, [propertyTypeChecked, subTypeChecked]);

  return (
    <Popover placement="bottom" closeOnBlur={true}>
      {({ isOpen, onClose }) => (
        <>
          <PopoverTrigger>
            <TriggerFiltersWrapper mobileLayout={props.mobileLayout}>
              <TriggerFiltersButton
                type="button"
                mobileLayout={props.mobileLayout}
              >
                <Text as="span" fontSize="14px">
                  {selectedFilters.count > 0
                    ? selectedFilters.count > 1
                      ? `${selectedFilters.displayFilter} (+${
                          selectedFilters.count - 1
                        })`
                      : selectedFilters.displayFilter
                    : "All types"}
                </Text>
                {selectedFilters.count === 0 ? (
                  <IoIosArrowDown
                    style={{
                      marginTop: "2px",
                    }}
                  />
                ) : null}
              </TriggerFiltersButton>
            </TriggerFiltersWrapper>
          </PopoverTrigger>
          <PopoverContent
          >
            <PopoverHeader
              paddingTop="8px"
              paddingBottom="8px"
              fontWeight={501}
              border={0}
              fontSize="1rem"
              lineHeight="1.25rem"
            >
              Select Properties / Businesses
            </PopoverHeader>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              <PropertyTypeFilterCheckboxes />
            </PopoverBody>
            <PopoverFooter
              border="0"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              pb={4}
            >
              <Button
                onClick={() => {
                  setPropertyTypeChecked([]);
                  setSubTypeChecked([]);
                  setExpandedSubTypes([]);
                  props.onSubmit();
                  onClose();
                }}
                size="sm"
                colorScheme="green"
                variant="ghost"
              >
                Clear
              </Button>
              <Button
                onClick={() => {
                  props.onSubmit();
                  onClose();
                }}
                leftIcon={<CheckCircleIcon />}
                size="sm"
                colorScheme="green"
                // color="green.500"
                variant="solid"
              >
                Apply
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

const PropertyTypeFilterCheckboxes = () => {
  const {
    propertyTypeChecked,
    setPropertyTypeChecked,
    subTypeChecked,
    setSubTypeChecked,
    expandedSubTypes,
    setExpandedSubTypes,
  } = useContext(QueryStateContext);
  return (
    <Stack spacing={2} bgColor="#fff" overflowY={"scroll"} maxH="300px">
      {propertyTypeOptions.map((thisType, thisIndex) => (
        <Box key={thisIndex}>
          <Flex justifyContent={"space-between"} w="100%">
            <Checkbox
              colorScheme="green"
              isChecked={propertyTypeChecked[thisType["value"]]}
              isIndeterminate={
                subTypeChecked[thisType["value"]] &&
                Array.isArray(subTypeChecked[thisType["value"]]) &&
                subTypeChecked[thisType["value"]].some(Boolean) &&
                !propertyTypeChecked[thisType["value"]]
              }
              onChange={(e) => {
                let typeCheckedCopy = { ...propertyTypeChecked };

                typeCheckedCopy[thisType["value"]] = e.target.checked;
                setPropertyTypeChecked({ ...typeCheckedCopy });

                // Remove subtypes for this property type from the selection
                // IE - if all of land is selected, we don't need the subtypes (redundant)
                // If all of land is unselected, then we definitely don't need the subtypes (not the criteria)
                let subTypeCopy = { ...subTypeChecked };
                subTypeCopy[thisType["value"]] = [];
                setSubTypeChecked({ ...subTypeCopy });

                // Expand children
                if (e.target.checked) {
                  let expandedSubTypesCopy = { ...expandedSubTypes };

                  expandedSubTypesCopy[thisType["value"]] = true;
                  setExpandedSubTypes({ ...expandedSubTypesCopy });
                }
              }}
              children={thisType["value"]}
            />

            {groupedSubtype[thisIndex]["options"]?.length > 0 &&
              groupedSubtype[thisIndex]["label"].toLowerCase() !==
                "sale of business" && (
                <IconButton
                  aria-label="expand"
                  variant="ghost"
                  size="sm"
                  icon={
                    expandedSubTypes[thisType["value"]] ? (
                      <SmallCloseIcon />
                    ) : (
                      <SmallAddIcon />
                    )
                  }
                  onClick={() => {
                    let expandedSubTypesCopy = { ...expandedSubTypes };

                    if (expandedSubTypes[thisType["value"]]) {
                      expandedSubTypesCopy[thisType["value"]] = false;
                      setExpandedSubTypes({ ...expandedSubTypesCopy });
                    } else {
                      expandedSubTypesCopy[thisType["value"]] = true;
                      setExpandedSubTypes({ ...expandedSubTypesCopy });
                    }
                  }}
                />
              )}
          </Flex>
          {/* To avoid mixed use */}
          {groupedSubtype[thisIndex]["options"]?.length > 0 &&
            groupedSubtype[thisIndex]["label"].toLowerCase() !==
              "sale of business" && (
              <Collapse
                // mt={4}
                in={expandedSubTypes[thisType["value"]]}
              >
                <Stack pl={6} mt={1} spacing={2}>
                  {groupedSubtype[thisIndex]["options"].map((opt, index) => (
                    <Checkbox
                      key={index}
                      colorScheme="green"
                      isChecked={
                        propertyTypeChecked[thisType["value"]] ||
                        subTypeChecked[thisType["value"]]?.includes(
                          opt["value"]
                        )
                      }
                      onChange={(e) => {
                        let subTypeCopy = { ...subTypeChecked };
                        if (e.target.checked) {
                          // Does this already exist in the objects? If so then add to it, if not then save it
                          if (subTypeCopy[thisType["value"]]) {
                            let thisSubTypeCopy =
                              subTypeCopy[thisType["value"]];
                            subTypeCopy[thisType["value"]] = [
                              ...thisSubTypeCopy,
                              opt["value"],
                            ];
                            setSubTypeChecked({ ...subTypeCopy });

                            // If all are checked for this subtype, make the type true overall
                            if (
                              subTypeCopy[thisType["value"]].length ==
                                groupedSubtype[thisIndex]["options"].length &&
                              subTypeCopy[thisType["value"]].every(Boolean)
                            ) {
                              let typeCheckedCopy = { ...propertyTypeChecked };

                              typeCheckedCopy[thisType["value"]] = true;

                              setPropertyTypeChecked({ ...typeCheckedCopy });

                              // And remove the subyptes for this type
                              // If all of land is unselected, then we definitely don't need the subtypes (not the criteria)
                              subTypeCopy[thisType["value"]] = [];
                              setSubTypeChecked({ ...subTypeCopy });
                            }
                          } else {
                            subTypeCopy[thisType["value"]] = [opt["value"]];
                            setSubTypeChecked({ ...subTypeCopy });
                          }
                        } else {
                          const index = subTypeCopy[thisType["value"]].indexOf(
                            opt["value"]
                          );
                          // Check if it exists
                          if (index > -1) {
                            subTypeCopy[thisType["value"]].splice(index, 1);
                            setSubTypeChecked({ ...subTypeCopy });

                            // If office checked is true, make it false
                            if (propertyTypeChecked[thisType["value"]]) {
                              let typeCheckedCopy = { ...propertyTypeChecked };
                              typeCheckedCopy[thisType["value"]] = false;
                              setPropertyTypeChecked({ ...typeCheckedCopy });
                            }
                          }

                          // The event when this subtype wasn't explicity selected but was showing up as such because the overall type was selected, remove this type from typeselected, and add all other subtypes to the selection but this particular one (since it is being removed)
                          else if (propertyTypeChecked[thisType["value"]]) {
                            // Remove this from typechecked
                            let typeCheckedCopy = { ...propertyTypeChecked };
                            typeCheckedCopy[thisType["value"]] = false;
                            setPropertyTypeChecked({ ...typeCheckedCopy });

                            // Add all subtypes but this one
                            let listOfAllOthers = [];

                            groupedSubtype[thisIndex]["options"].forEach(
                              (o) => {
                                if (o["value"] != opt["value"]) {
                                  listOfAllOthers.push(o["value"]);
                                }
                              }
                            );

                            subTypeCopy[thisType["value"]] = listOfAllOthers;
                            setSubTypeChecked({ ...subTypeCopy });
                          }
                        }
                      }}
                      children={opt["value"]}
                    />
                  ))}
                  {/* <Checkbox
                              isChecked={subTypeChecked[1]}
                              onChange={e => setSubTypeChecked([subTypeChecked[0], e.target.checked])}
                              children="Child Checkbox 2"
                              /> */}
                </Stack>
              </Collapse>
            )}
        </Box>
      ))}
    </Stack>
  );
};
